import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { type TooltipContentProps, type TooltipProps } from '@radix-ui/react-tooltip';

import { cn } from '../utils';

interface DefaultTooltipProps
  extends Pick<TooltipProps, 'open' | 'defaultOpen' | 'onOpenChange' | 'delayDuration'>,
    Omit<TooltipContentProps, 'content'> {
  children: React.ReactNode;
  content: React.ReactNode;
  arrow?: boolean;
}

export function DefaultTooltip({
  delayDuration = 700,
  arrow = true,
  children,
  content,
  open,
  defaultOpen,
  onOpenChange,
  className,
  ...props
}: DefaultTooltipProps) {
  return (
    <TooltipPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration}
    >
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side="top" align="center" className="z-50 drop-shadow-sm" {...props}>
          <div className={cn('bg-surface-card max-h-40 overflow-y-auto rounded-sm p-sm', className)}>{content}</div>
          {arrow ? <TooltipPrimitive.Arrow width={11} height={5} className="fill-grey-white" /> : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const Tooltip = {
  Default: DefaultTooltip,
  Provider: TooltipPrimitive.Provider,
};

const TooltipContent = ({
  ref,
  className,
  sideOffset = 4,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
  ref?: React.Ref<React.ElementRef<typeof TooltipPrimitive.Content>>;
}) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'border-grey-border bg-surface-card text-grey-primary animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 origin-radix-tooltip overflow-hidden rounded-sm border p-xs text-xs',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
);

export const TooltipV2 = {
  Provider: TooltipPrimitive.Provider,
  Tooltip: TooltipPrimitive.Root,
  TooltipTrigger: TooltipPrimitive.Trigger,
  TooltipContent,
};
